/**
 * Pure rules of the shared dialog shell (core/ui/dialog-shell). Every popup of the app goes
 * through PopupAndNotifService, which opens dialogs built on that shell: these rules decide the
 * tone, the icon and the button style so that a confirmation looks the same on every page.
 */

/** Visual tone of a dialog: drives the header icon and the confirm button color. */
export type DialogTone = 'default' | 'danger' | 'warning' | 'info' | 'success';

/** Style of a dialog button (class `ap-dialog-btn--<style>`). */
export type DialogButtonStyle = 'primary' | 'danger' | 'neutral' | 'light';

/** Material colors still passed by existing callers of openConfirmation(). */
export type LegacyDialogColor = 'primary' | 'accent' | 'warn' | undefined | null;

const TONE_ICONS: Record<DialogTone, string | null> = {
  default: null,
  danger: 'alert-triangle',
  warning: 'alert-triangle',
  info: 'info-circle',
  success: 'circle-check',
};

/** The header icon of a tone; an explicit icon always wins. `null` = no icon. */
export function dialogToneIcon(tone: DialogTone | null | undefined, explicitIcon?: string | null): string | null {
  const icon = (explicitIcon ?? '').toString().trim();
  if (icon) {
    return icon;
  }
  return TONE_ICONS[tone ?? 'default'] ?? null;
}

/**
 * The tone of a confirmation from what the caller passed: an explicit tone wins, otherwise a
 * `warn` confirm color (the historical way to ask for a destructive confirmation) means danger.
 */
export function confirmationTone(tone: DialogTone | null | undefined, confirmColor: LegacyDialogColor): DialogTone {
  if (tone) {
    return tone;
  }
  return confirmColor === 'warn' ? 'danger' : 'default';
}

/**
 * Style of the confirm button: pink only when the action is destructive. Every other confirmation
 * uses the primary color.
 */
export function confirmButtonStyle(tone: DialogTone): DialogButtonStyle {
  return tone === 'danger' ? 'danger' : 'primary';
}

/**
 * Style of the cancel button: neutral whatever the tone of the dialog. It used to be filled pink,
 * which made "Cancel" read as the dangerous action (decision of 2026-10-13,
 * docs/ArturPro/UI_DESIGN_SYSTEM.md).
 */
export function cancelButtonStyle(_tone?: DialogTone | null): DialogButtonStyle {
  return 'neutral';
}

/**
 * Tone of a confirmation asked through the legacy HelperService API, which took a SweetAlert-like
 * icon name ('warning', 'delete', 'info-triangle', a tabler name…).
 */
export function legacyConfirmIconTone(icon: string | null | undefined): DialogTone {
  const value = (icon ?? '').toString().trim().toLowerCase();
  if (value === 'delete' || value === 'trash' || value === 'error' || value === 'danger') {
    return 'danger';
  }
  if (value === 'warning' || value === 'info-triangle' || value === 'alert-triangle') {
    return 'warning';
  }
  if (value === 'info' || value === 'question' || value === 'info-circle') {
    return 'info';
  }
  if (value === 'success' || value === 'circle-check') {
    return 'success';
  }
  return 'default';
}
